import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import * as addTeamActions from 'actions/addTeam';
import { unarchiveJob } from 'actions/employeeView';
import { createOneTimeEvent as createOTE } from 'actions/session';

import { getCurrentLocationAccessLevelOptions } from 'selectors/addTeam';
import {
  buildGetDrawerOpen,
  buildGetDrawerProps,
  getPacketProps,
} from 'selectors/drawers';
import { getEmployeeViewUserFromState } from 'selectors/employeeView';
import {
  getCanViewWages,
  getCurrentCompanyHasNoJobRequests,
  getCurrentLocationId,
  getHasOnboardingDocs,
  getIsPayrollEnrolled,
  getRolloutEnabled,
  getUserOneTimeEventDone,
} from 'selectors/session';

import { ADD_TEAM_INDIVIDUAL_DRAWER } from 'features/drawers/keys';
import { createCustomDocument } from 'features/employeeOnboarding/slices';
import { fetchTeamReadiness } from 'features/payroll/PayrollInfo/PayrollInfoSlice';
import {
  fetchTeamMember,
  getEditingUser,
  getIsFetchingTeamMember,
  getIsUpdatingTeamMember,
  payrollTeamSetupActions,
  updateTeamMember,
} from 'features/payroll/SelfServeSetupView/TasksDashboard/Components/TeamSetup/teamSetupSlice';

import Box from 'components/Box';
import IconButton from 'components/clickables/IconButton';
import Drawer from 'components/Drawer';
import withDispatchOnNavigation from 'components/hocs/withDispatchOnNavigation';
import withUxTracking from 'components/hocs/withUxTracking';
import LoadingIndicator from 'components/LoadingIndicator';
import { MobileAndTablet } from 'components/MediaQuery';

import cxHelpers from 'util/className';
import { error as flashError } from 'util/flashNotice';
import {
  ELEMENTS,
  EVENT_ACTIONS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { buildStateValidator, PHONE_EXTENDED_REGEX } from 'util/validators';

import { enrollEntityInExperiment } from '../../experiments/actions';
import { getViewLogic } from '../../experiments/selectors';

import FormView from './FormView';
import {
  canRequestInformation,
  getUserPayrollEligibility,
  invalidPayrollInfo,
  jobAttributes,
  shouldSuccessViewTimeout,
  showHiringBanner,
  userAndJobData,
} from './helpers';
import PayrollInfoView from './PayrollInfoView';
import SuccessView from './SuccessView';

const INIT_STATE = {
  id: null,
  packet: 'w2',
  jobTaxClassification: null,
  contractorType: null,
  fourTensRuleEnabled: false,
  hasITIN: false,
  hasNoActivePayments: false,
  hasPaidPayrollRunsForJob: false,
  hasJobsAtMoreThanOneCompany: false,
  firstName: '',
  lastName: '',
  fullName: '',
  level: 'Employee',
  hireDate: null,
  phone: '',
  email: '',
  wageAttributes: [],
  errors: {},
  sendSms: true,
  view: 'Form',
  addMore: false,
  avatarUrl: '',
  includedInPayroll: true,
  requestInformation: null,
  canRequestInformation: null,
  isEligibleForPayroll: null,
  formKey: null,
  submitting: false,
  hiredFrom: '',
};

const FIRST_NAME_MAXIMUM = 40;

const validators = {
  firstName: {
    presence: {
      allowEmpty: false,
    },
    length: {
      maximum: FIRST_NAME_MAXIMUM,
    },
  },
  email: {
    email: true,
  },
  phone: {
    length: {
      minimum: 10,
    },
    format: {
      pattern: PHONE_EXTENDED_REGEX,
    },
  },
  hireDate: {
    hireDateRange: true,
  },
};

const validateState = buildStateValidator(validators);

@withUxTracking('Team', 'Add Team Drawer')
@withDispatchOnNavigation(addTeamActions.closeIndividualDrawer(false))
@connect(
  (state, props) => ({
    open: buildGetDrawerOpen(ADD_TEAM_INDIVIDUAL_DRAWER)(state, props),
    currentLocationId: getCurrentLocationId(state, props),
    noJobPosts: getCurrentCompanyHasNoJobRequests(state, props),
    hasOnboardingDocsAccess: getHasOnboardingDocs(state, props),
    isHiringBannerEnabled: getRolloutEnabled(state, 'hiring_job_drawer'),
    packetProps: getPacketProps(state, props),
    canViewWages: getCanViewWages(state, props),
    isPayrollEnrolled: getIsPayrollEnrolled(state, props),
    editingUser: getEditingUser(state, props),
    drawerProps: buildGetDrawerProps(state, props),
    isFetchingTeamMember: getIsFetchingTeamMember(state, props),
    isUpdatingTeamMember: getIsUpdatingTeamMember(state, props),
    accessLevelOptions: getCurrentLocationAccessLevelOptions(state),
    firstTimeInviting: !getUserOneTimeEventDone(
      state,
      'timesheets_guided_banner_step1_clicked'
    ),
    hiringBannerDismissed: getUserOneTimeEventDone(state, 'hiring_job_drawer'),
    hiringSourceExperiment:
      getViewLogic(state, 'hiring_candiate_source_information_gathering') === 1,
    user: getEmployeeViewUserFromState(state, props),
  }),
  {
    closeDrawer: addTeamActions.closeIndividualDrawer,
    toggleChooseHowDrawer: addTeamActions.toggleChooseHowDrawer,
    createEmployee: addTeamActions.createEmployee,
    onCreateCustomDocument: createCustomDocument,
    unarchiveJob,
    resetEditingUser: payrollTeamSetupActions.resetEditingUser,
    updateTeamMember,
    fetchTeamReadiness,
    fetchTeamMember,
    createOneTimeEvent: createOTE,
    onEnroll: enrollEntityInExperiment,
  }
)
@cxHelpers('AddIndividualDrawer')
export default class AddIndividualDrawer extends PureComponent {
  static propTypes = {
    hasOnboardingDocsAccess: PropTypes.bool.isRequired,
    open: PropTypes.bool.isRequired,
    closeDrawer: PropTypes.func.isRequired,
    toggleChooseHowDrawer: PropTypes.func.isRequired,
    createEmployee: PropTypes.func.isRequired,
    currentLocationId: PropTypes.number,
    noJobPosts: PropTypes.bool,
    unarchiveJob: PropTypes.func,
    hiringBannerDismissed: PropTypes.bool,
    hiringSourceExperiment: PropTypes.bool,
    isHiringBannerEnabled: PropTypes.bool,
    isPayrollEnrolled: PropTypes.bool.isRequired,
    canViewWages: PropTypes.bool,
    trackUxEvent: PropTypes.func.isRequired,
    editingUser: PropTypes.object,
    drawerProps: PropTypes.object.isRequired,
    resetEditingUser: PropTypes.func.isRequired,
    updateTeamMember: PropTypes.func.isRequired,
    fetchTeamReadiness: PropTypes.func.isRequired,
    isFetchingTeamMember: PropTypes.bool,
    isUpdatingTeamMember: PropTypes.bool,
    accessLevelOptions: PropTypes.array,
    onCreateCustomDocument: PropTypes.func,
    fetchTeamMember: PropTypes.func.isRequired,
    onEnroll: PropTypes.func,
    user: PropTypes.object,
    packetProps: PropTypes.object,
    firstTimeInviting: PropTypes.bool,
    createOneTimeEvent: PropTypes.func,
  };

  state = INIT_STATE;

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!!nextProps.editingUser && nextProps.editingUser.id !== prevState.id) {
      return {
        ...nextProps.editingUser,
        wageAttributes: [...nextProps.editingUser.wageAttributes],
      };
    }
    return null;
  }

  componentDidMount() {
    this.props.onRef(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open !== this.props.open && this.props.open) {
      this.props.trackUxEvent({
        eventAction: EVENT_ACTIONS.ADD_TEAM_DRAWER_SHOWN,
      });
      this.props.onEnroll(['hiring_candiate_source_information_gathering']);
    }
  }

  handleJobTaxClassificationChange = value => {
    this.setState(
      {
        jobTaxClassification: value || null,
        packet: value,
      },
      () => {
        this.validateUserPayrollEligibility();
        this.validateRequestInformation();
      }
    );
  };

  handleContractorTypeChange = value => {
    this.setState(
      {
        contractorType: value || null,
      },
      () => {
        this.validateUserPayrollEligibility();
        this.validateRequestInformation();
      }
    );
  };

  setFieldValue = (key, value) => {
    const new_state = {};
    const keyMapper = {
      four_tens_rule_enabled: 'fourTensRuleEnabled',
    };

    new_state[keyMapper[key]] = value || false;

    this.setState(new_state);
  };

  handlePacketChange = value => {
    this.setState({ packet: value });
  };

  handleClose = () => {
    sessionStorage.setItem('timesheets_guided_banner_step1_clicked', false);
    this.resetState();

    // Force re-initialization of the Form react component when the drawer closes
    // to clear lingering states in sub-components
    setTimeout(
      () => this.setState({ formKey: Math.floor(1000 + Math.random() * 9000) }),
      0
    );
    this.props.closeDrawer();
  };

  openForm = () => {
    this.setState({ view: 'Form' });
  };

  validate = field => {
    this.setState({ errors: validateState(this.state, field) });
  };

  debouncedResetErrors = debounce(this.resetErrors, 100);

  resetErrors(field) {
    if (this.state.errors[field]) {
      this.validate(field);
    }
  }

  resetState() {
    setTimeout(() => this.setState(INIT_STATE), 0);
    this.props.resetEditingUser();
  }

  handleBlur = field => {
    if (
      (field === 'phone' && !this.state.phone) ||
      (field === 'email' && !this.state.email)
    ) {
      return;
    }
    this.validate(field);
  };

  validateRequestInformation = () => {
    const canRequest = canRequestInformation({
      phone: this.state.phone,
      email: this.state.email,
      jobTaxClassification: this.state.jobTaxClassification,
      contractorType: this.state.contractorType,
      requestInformation: this.state.requestInformation,
    });
    this.setState({ canRequestInformation: canRequest });
  };

  guidedBannerOTE = invitedUser => {
    if (
      this.props.firstTimeInviting &&
      window.sessionStorage.getItem(
        'timesheets_guided_banner_step1_clicked'
      ) === 'true' &&
      invitedUser
    ) {
      this.props.createOneTimeEvent('timesheets_guided_banner_step1_clicked');
    }
  };

  validateUserPayrollEligibility = () => {
    const eligibility = getUserPayrollEligibility({
      includedInPayroll: this.state.includedInPayroll,
      jobTaxClassification: this.state.jobTaxClassification,
      hireDate: this.state.hireDate,
      wageRate: this.state.wageAttributes[0]?.wage_rate,
      contractorType: this.state.contractorType,
    });
    this.setState({ isEligibleForPayroll: eligibility });
  };

  handleChange = (field, value) => {
    const inputValue = typeof value === 'string' ? value.trim() || null : value;
    this.setState({ [field]: inputValue }, () => {
      this.debouncedResetErrors(field);
      if (field === 'hireDate') {
        this.validate('hireDate');
      }

      this.validateRequestInformation();
      this.validateUserPayrollEligibility();

      if (field === 'includedInPayroll' && this.props.editingUser?.id) {
        this.handleSubmitUser(true);
      }
    });
  };

  handleMoveBack = () => {
    this.handleClose();
    this.props.toggleChooseHowDrawer(true);
  };

  // jobAttributes moved to helpers.ts

  // userAndJobData moved to helpers.ts

  resetSubmittingFlag = () => this.setState({ submitting: false });

  createUser = (params, callback) => {
    this.props.createEmployee(
      params,
      id => {
        this.createEmployeeCallback(id, callback);
        this.resetSubmittingFlag();
      },
      this.resetSubmittingFlag
    );
  };

  createUserAndJob = callback =>
    this.createUser(
      userAndJobData({
        firstName: this.state.firstName,
        lastName: this.state.lastName,
        phone: this.state.phone,
        email: this.state.email,
        requestInformation: this.state.requestInformation,
        sendSms: this.state.sendSms,
        jobAttributesData: jobAttributes({
          wageAttributes: this.state.wageAttributes,
          jobTaxClassification: this.state.jobTaxClassification,
          contractorType: this.state.contractorType,
          fourTensRuleEnabled: this.state.fourTensRuleEnabled,
          packet: this.state.packet,
          hireDate: this.state.hireDate,
          includedInPayroll: this.state.includedInPayroll,
          hiredFrom: this.state.hiredFrom,
          level: this.state.level,
          currentLocationId: this.props.currentLocationId,
        }),
      }),
      callback
    );

  conditionalTimeout = callback => {
    if (
      shouldSuccessViewTimeout({
        isHiringBannerEnabled: this.props.isHiringBannerEnabled,
        hiringBannerDismissed: this.props.hiringBannerDismissed,
        addMore: this.state.addMore,
      })
    ) {
      setTimeout(callback, 2000);
    }
  };

  createEmployeeCallback = (id, callback) => {
    if (this.state.requestInformation !== 'manual') {
      this.setState({ view: 'Success' });
    }

    if (this.props.isPayrollEnrolled) {
      if (this.state.requestInformation === 'manual') {
        this.props.fetchTeamMember(id);
        this.setState({ view: 'PayrollInfo' });
      } else {
        this.conditionalTimeout(callback);
      }

      this.props.fetchTeamReadiness();
    } else {
      this.conditionalTimeout(callback);
    }
  };

  save = callback => {
    const newCallback = () => {
      if (this.props.noJobPosts) {
        this.props.trackUxEvent({
          eventAction: EVENT_ACTIONS.HIRING_SURVEY_NEW_EMPLOYEE_ADDED,
        });
      }

      callback();
    };

    this.setState({ submitting: true });

    return this.createUserAndJob(newCallback);
  };

  handleSubmitUser = (fetchTeamReadinessOnly = false) => {
    const { editingUser } = this.props;
    if (editingUser?.id) {
      this.handleUpdateUser(fetchTeamReadinessOnly);
    } else {
      this.save(() => {
        this.handleClose();
        this.props.fetchTeamReadiness();
      });
    }
  };

  handleUpdateUser = (fetchTeamReadinessOnly = true) => {
    const { editingUser } = this.props;
    if (!editingUser.id) {
      return;
    }

    const data = userAndJobData({
      firstName: this.state.firstName,
      lastName: this.state.lastName,
      phone: this.state.phone,
      email: this.state.email,
      requestInformation: this.state.requestInformation,
      sendSms: this.state.sendSms,
      jobAttributesData: jobAttributes({
        wageAttributes: this.state.wageAttributes,
        jobTaxClassification: this.state.jobTaxClassification,
        contractorType: this.state.contractorType,
        fourTensRuleEnabled: this.state.fourTensRuleEnabled,
        packet: this.state.packet,
        hireDate: this.state.hireDate,
        includedInPayroll: this.state.includedInPayroll,
        hiredFrom: this.state.hiredFrom,
        level: this.state.level,
        currentLocationId: this.props.currentLocationId,
      }),
    });
    data.user.id = editingUser.id;
    data.user.all_jobs_attributes[0].id = editingUser.jobId;
    data.user.all_jobs_attributes[0].included_in_payroll =
      this.state.includedInPayroll;
    data.request_information = this.state.requestInformation;

    this.props
      .updateTeamMember({ userId: editingUser.id, data })
      .then(response => {
        if (response.meta.requestStatus === 'rejected') {
          flashError(response.payload.errors.join('. '));
          return;
        }

        // Do not go to the second view or close the drawer
        // If fetchTeamReadinessOnly is true
        if (
          typeof fetchTeamReadinessOnly !== 'boolean' ||
          !fetchTeamReadinessOnly
        ) {
          if (this.state.requestInformation === 'manual') {
            this.setState({ view: 'PayrollInfo' });
          } else {
            this.handleClose();
          }
        }

        this.props.fetchTeamReadiness();
      });
  };

  handleUnarchiveJob = () =>
    this.props
      .unarchiveJob(this.state.existingJobId, this.state.existingUser.id)
      .then(this.handleClose());

  handleSaveAndClose = () => {
    const invitedUser = this.state.sendSms;
    this.props.trackUxEvent({
      eventAction: EVENT_ACTIONS.SAVE_AND_CLOSE_CLICKED,
    });
    this.setState({ addMore: false });
    this.save(() => {
      this.guidedBannerOTE(invitedUser);
      this.handleClose();
    });
  };

  handleSaveAndAdd = () => {
    const invitedUser = this.state.sendSms;
    this.props.trackUxEvent({
      eventAction: EVENT_ACTIONS.SAVE_AND_ADD_ANOTHER_CLICKED,
    });
    this.setState({ addMore: true });
    this.save(() => {
      this.guidedBannerOTE(invitedUser);

      this.resetState();
      if (this.props.drawerProps.isPayrollSetup) {
        this.props.fetchTeamReadiness();
      }
    });
  };

  handleOnClickBackButton = () => {
    this.setState({ view: 'Form' });
  };

  handleOnClickDone = () => {
    this.handleClose();
    this.props.fetchTeamReadiness();
  };

  handleClickClose = () => {
    this.props.trackUxEvent({
      eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
      actionType: TRACK_ACTION_TYPES.CLICK,
      properties: {
        element: ELEMENTS.MAYBE_LATER_BUTTON,
      },
    });
    this.handleClose();
  };

  handleClickDiscoverNow = () => {
    this.props.trackUxEvent({
      eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
      actionType: TRACK_ACTION_TYPES.CLICK,
      properties: {
        element: ELEMENTS.DISCOVER_NOW_BUTTON,
      },
    });
  };

  handleClickDontShowAgain = () => {
    this.props.trackUxEvent({
      eventAction: EVENT_ACTIONS.BUTTON_CLICKED,
      actionType: TRACK_ACTION_TYPES.CLICK,
      properties: {
        element: ELEMENTS.DONT_SHOW_AGAIN_BUTTON,
      },
    });
    this.props.createOneTimeEvent('hiring_job_drawer');
    this.handleClose();
  };

  renderFormView(mobileTablet) {
    const {
      editingUser,
      canViewWages,
      hasOnboardingDocsAccess,
      isPayrollEnrolled,
      drawerProps: { isPayrollSetup },
      isUpdatingTeamMember,
      packetProps: {
        numberOfDocumentsInContractorTemplate,
        numberOfDocumentsInEmployeeTemplate,
        newHireOnboardingActivated,
      },
      hiringSourceExperiment,
      user,
      accessLevelOptions,
    } = this.props;

    return (
      <FormView
        // State props
        hasPaidPayrollRunsForJob={this.state.hasPaidPayrollRunsForJob}
        hasNoActivePayments={this.state.hasNoActivePayments}
        fourTensRuleEnabled={this.state.fourTensRuleEnabled}
        hasITIN={this.state.hasITIN}
        jobTaxClassification={this.state.jobTaxClassification}
        contractorType={this.state.contractorType}
        firstName={this.state.firstName}
        lastName={this.state.lastName}
        phone={this.state.phone}
        email={this.state.email}
        wageAttributes={this.state.wageAttributes}
        hireDate={this.state.hireDate}
        sendSms={this.state.sendSms}
        packet={this.state.packet}
        level={this.state.level}
        includedInPayroll={this.state.includedInPayroll}
        requestInformation={this.state.requestInformation}
        errors={this.state.errors}
        canRequestInformation={this.state.canRequestInformation}
        isEligibleForPayroll={this.state.isEligibleForPayroll}
        userId={this.state.id || this.state.formKey}
        firstNameMaximum={FIRST_NAME_MAXIMUM}
        hiredFrom={this.state.hiredFrom}
        submitting={this.state.submitting}
        // Props from parent
        editingUser={editingUser}
        canViewWages={canViewWages}
        hasOnboardingDocsAccess={hasOnboardingDocsAccess}
        isPayrollEnrolled={isPayrollEnrolled}
        isPayrollSetup={isPayrollSetup}
        isUpdatingTeamMember={isUpdatingTeamMember}
        numberOfDocumentsInContractorTemplate={
          numberOfDocumentsInContractorTemplate
        }
        numberOfDocumentsInEmployeeTemplate={
          numberOfDocumentsInEmployeeTemplate
        }
        newHireOnboardingActivated={newHireOnboardingActivated}
        hiringSourceExperiment={hiringSourceExperiment}
        user={user}
        accessLevelOptions={accessLevelOptions}
        // Event handlers
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handlePacketChange={this.handlePacketChange}
        handleJobTaxClassificationChange={this.handleJobTaxClassificationChange}
        handleContractorTypeChange={this.handleContractorTypeChange}
        setFieldValue={this.setFieldValue}
        handleMoveBack={this.handleMoveBack}
        handleSubmitUser={this.handleSubmitUser}
        handleSaveAndClose={this.handleSaveAndClose}
        handleSaveAndAdd={this.handleSaveAndAdd}
        invalidPayrollInfo={invalidPayrollInfo}
        validateUserPayrollEligibility={this.validateUserPayrollEligibility}
        cxEl={this.cxEl}
        // Mobile/tablet flag
        mobileTablet={mobileTablet}
      />
    );
  }

  renderPayrollInfoView(mobileTablet) {
    return (
      <PayrollInfoView
        editingUser={this.props.editingUser}
        onBack={this.handleOnClickBackButton}
        onDone={this.handleOnClickDone}
        invalidPayrollInfo={invalidPayrollInfo}
        mobileTablet={mobileTablet}
        {...this.props}
      />
    );
  }

  renderSuccessView() {
    return (
      <SuccessView
        firstName={this.state.firstName}
        lastName={this.state.lastName}
        showHiringBanner={showHiringBanner({
          addMore: this.state.addMore,
          isHiringBannerEnabled: this.props.isHiringBannerEnabled,
          hiringBannerDismissed: this.props.hiringBannerDismissed,
        })}
        onClose={this.handleClickClose}
        onDiscoverNow={this.handleClickDiscoverNow}
        onDontShowAgain={this.handleClickDontShowAgain}
        isHiringBannerEnabled={this.props.isHiringBannerEnabled}
      />
    );
  }

  setRequestedInformation() {
    if (invalidPayrollInfo(this.props.user)) {
      this.setState({ requestInformation: null });
    } else {
      this.setState({ requestInformation: 'manual' });
    }
  }

  setOpenedDrawer(flag) {
    this.setState({ openedDrawer: flag });
  }

  renderView(mobileTablet) {
    // If drawer was just opened, set correct value for requestInformation
    // To avoid stale state when switching between users while drawer is open
    if (this.state.openedDrawer) {
      this.setRequestedInformation();
      this.validateRequestInformation();
    }

    if (this.props.isFetchingTeamMember) {
      this.setOpenedDrawer(true);
      return <LoadingIndicator centered mt64 pt64 />;
    }

    this.setOpenedDrawer(false);
    return this[`render${this.state.view}View`](mobileTablet);
  }

  render() {
    const { open } = this.props;

    if (!open) {
      return null;
    }

    return (
      <MobileAndTablet>
        {(mobile, tablet) => (
          <Drawer open={open} width={mobile || tablet ? '100%' : '550px'}>
            <Box
              className={this.cx({ open })}
              w={mobile || tablet ? '100%' : 550}
              h="100%"
            >
              <Box absolute top={5} right={5} z={2}>
                <IconButton
                  iconColor="grayMediumDark"
                  size="md"
                  type="mdClose"
                  onClick={this.handleClose}
                />
              </Box>
              {this.renderView(mobile || tablet)}
            </Box>
          </Drawer>
        )}
      </MobileAndTablet>
    );
  }
}
